import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { ArrowLeftRight, Plus, Search, RefreshCw, Check, ChevronsUpDown, RotateCcw, Package, Users } from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TAGLIE = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL', 'Unica'];
const CONDIZIONI = [
  { value: 'buono', label: 'Buono' },
  { value: 'danneggiato', label: 'Danneggiato' },
  { value: 'da_sostituire', label: 'Da Sostituire' },
  { value: 'fuori_uso', label: 'Fuori Uso' }
];

export default function MagazzinoAssegnazioni() {
  const [assegnazioni, setAssegnazioni] = useState([]);
  const [articoli, setArticoli] = useState([]);
  const [volontari, setVolontari] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filtroStato, setFiltroStato] = useState('attive');
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [showResoDialog, setShowResoDialog] = useState(false);
  const [selectedAssegnazione, setSelectedAssegnazione] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Form nuova assegnazione
  const [selectedArticoloId, setSelectedArticoloId] = useState('');
  const [selectedVolontarioId, setSelectedVolontarioId] = useState('');
  const [quantita, setQuantita] = useState(1);
  const [taglia, setTaglia] = useState('');
  const [noteAssegnazione, setNoteAssegnazione] = useState('');
  const [artSearchOpen, setArtSearchOpen] = useState(false);
  const [volSearchOpen, setVolSearchOpen] = useState(false);

  // Form restituzione
  const [condizioneReso, setCondizioneReso] = useState('buono');
  const [noteReso, setNoteReso] = useState('');

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } : {};
  };

  const fetchAssegnazioni = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const params = new URLSearchParams();
      params.append('stato', filtroStato);
      if (search) params.append('search', search);

      const res = await fetch(`${API_URL}/api/magazzino/assegnazioni?${params}`, { headers, credentials: 'include' });
      if (res.ok) { const d = await res.json(); setAssegnazioni(d.assegnazioni || []); }
    } catch (error) {
      console.error('Errore:', error);
    } finally {
      setLoading(false);
    }
  }, [search, filtroStato]);

  const fetchArticoliEVolontari = useCallback(async () => {
    const headers = getAuthHeaders();
    const [artRes, volRes] = await Promise.all([
      fetch(`${API_URL}/api/magazzino/articoli`, { headers, credentials: 'include' }),
      fetch(`${API_URL}/api/volontari`, { headers, credentials: 'include' })
    ]);
    if (artRes.ok) { const d = await artRes.json(); setArticoli(d.articoli || []); }
    if (volRes.ok) {
      const d = await volRes.json();
      setVolontari(Array.isArray(d) ? d : d.volontari || []);
    }
  }, []);

  useEffect(() => { fetchAssegnazioni(); fetchArticoliEVolontari(); }, [fetchAssegnazioni, fetchArticoliEVolontari]);

  const handleAssegna = async () => {
    if (!selectedArticoloId || !selectedVolontarioId) {
      toast.error('Seleziona articolo e volontario');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/magazzino/assegnazioni`, {
        method: 'POST', headers: getAuthHeaders(), credentials: 'include',
        body: JSON.stringify({
          articolo_id: selectedArticoloId,
          volontario_id: selectedVolontarioId,
          quantita, taglia: taglia || null,
          note: noteAssegnazione
        })
      });
      if (res.ok) {
        toast.success('Materiale assegnato con successo');
        setShowNewDialog(false);
        resetForm();
        fetchAssegnazioni();
        fetchArticoliEVolontari();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Errore nell\'assegnazione');
      }
    } catch { toast.error('Errore di connessione'); }
    finally { setSubmitting(false); }
  };

  const handleRestituisci = async () => {
    if (!selectedAssegnazione) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/magazzino/assegnazioni/${selectedAssegnazione.assegnazione_id}/restituisci`, {
        method: 'POST', headers: getAuthHeaders(), credentials: 'include',
        body: JSON.stringify({ condizione_reso: condizioneReso, note_reso: noteReso })
      });
      if (res.ok) {
        toast.success('Restituzione registrata');
        setShowResoDialog(false);
        setSelectedAssegnazione(null);
        setCondizioneReso('buono');
        setNoteReso('');
        fetchAssegnazioni();
        fetchArticoliEVolontari();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Errore nella restituzione');
      }
    } catch { toast.error('Errore di connessione'); }
    finally { setSubmitting(false); }
  };

  const resetForm = () => {
    setSelectedArticoloId('');
    setSelectedVolontarioId('');
    setQuantita(1);
    setTaglia('');
    setNoteAssegnazione('');
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('it-IT') : '-';

  const getCondizioneBadge = (c) => {
    const map = {
      buono: 'bg-green-100 text-green-700',
      danneggiato: 'bg-orange-100 text-orange-700',
      da_sostituire: 'bg-red-100 text-red-700',
      fuori_uso: 'bg-gray-100 text-gray-700'
    };
    return <Badge className={map[c] || 'bg-gray-100 text-gray-700'}>{c?.replace('_', ' ') || c}</Badge>;
  };

  const attive = assegnazioni.filter(a => !a.data_reso).length;
  const restituite = assegnazioni.filter(a => a.data_reso).length;

  return (
    <div className="space-y-6" data-testid="magazzino-assegnazioni">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <ArrowLeftRight className="w-6 h-6 text-indigo-600" />
            Assegnazioni Materiale
          </h1>
          <p className="text-sm text-slate-500 mt-1">Consegna e restituzione dotazioni ai volontari</p>
        </div>
        <Button onClick={() => { resetForm(); setShowNewDialog(true); }} data-testid="nuova-assegnazione-btn">
          <Plus className="w-4 h-4 mr-2" /> Nuova Assegnazione
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
              <Package className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{attive}</p>
              <p className="text-xs text-slate-500">Assegnazioni Attive</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
              <RotateCcw className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{restituite}</p>
              <p className="text-xs text-slate-500">Restituiti</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filtri */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Cerca articolo o volontario..." className="pl-10" value={search}
                onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && fetchAssegnazioni()} data-testid="search-input" />
            </div>
            <Select value={filtroStato} onValueChange={setFiltroStato}>
              <SelectTrigger className="w-[150px]" data-testid="filtro-stato">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="attive">In consegna</SelectItem>
                <SelectItem value="restituite">Restituiti</SelectItem>
                <SelectItem value="tutte">Tutti</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchAssegnazioni}><RefreshCw className="w-4 h-4" /></Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabella */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Caricamento...</div>
          ) : assegnazioni.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <ArrowLeftRight className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="font-medium">Nessuna assegnazione</p>
              <p className="text-sm mt-1">Assegna materiale ai volontari cliccando "Nuova Assegnazione"</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Articolo</TableHead>
                  <TableHead>Volontario</TableHead>
                  <TableHead className="hidden md:table-cell">Taglia</TableHead>
                  <TableHead className="text-center">Qtà</TableHead>
                  <TableHead>Consegna</TableHead>
                  <TableHead>Reso</TableHead>
                  <TableHead className="hidden md:table-cell">Condizione</TableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {assegnazioni.map((a) => (
                  <TableRow key={a.assegnazione_id} data-testid={`assegnazione-row-${a.assegnazione_id}`}>
                    <TableCell>
                      <div className="font-semibold">{a.articolo_nome}</div>
                      <div className="text-xs text-slate-500">{a.articolo_categoria}</div>
                    </TableCell>
                    <TableCell>{a.volontario_nome}</TableCell>
                    <TableCell className="hidden md:table-cell">{a.taglia || '-'}</TableCell>
                    <TableCell className="text-center font-mono">{a.quantita}</TableCell>
                    <TableCell>{formatDate(a.data_consegna)}</TableCell>
                    <TableCell>{a.data_reso ? formatDate(a.data_reso) : <Badge className="bg-amber-100 text-amber-700">In consegna</Badge>}</TableCell>
                    <TableCell className="hidden md:table-cell">{a.condizione_reso ? getCondizioneBadge(a.condizione_reso) : '-'}</TableCell>
                    <TableCell className="text-right">
                      {!a.data_reso && (
                        <Button size="sm" variant="outline" onClick={() => { setSelectedAssegnazione(a); setShowResoDialog(true); }}
                          data-testid={`reso-btn-${a.assegnazione_id}`}>
                          <RotateCcw className="h-4 w-4 mr-1" /> Reso
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Dialog Nuova Assegnazione */}
      <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nuova Assegnazione Materiale</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {/* Articolo */}
            <div className="grid gap-2">
              <Label>Articolo *</Label>
              <Popover open={artSearchOpen} onOpenChange={setArtSearchOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" role="combobox" className="justify-between font-normal" data-testid="select-articolo">
                    {selectedArticoloId ? articoli.find(a => a.articolo_id === selectedArticoloId)?.nome || 'Selezionato' : 'Cerca articolo...'}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0">
                  <Command>
                    <CommandInput placeholder="Cerca..." />
                    <CommandList>
                      <CommandEmpty>Nessun articolo trovato</CommandEmpty>
                      <CommandGroup>
                        {articoli.filter(a => a.quantita_disponibile > 0).map(a => (
                          <CommandItem key={a.articolo_id} onSelect={() => { setSelectedArticoloId(a.articolo_id); setArtSearchOpen(false); }}>
                            <Check className={cn("mr-2 h-4 w-4", selectedArticoloId === a.articolo_id ? "opacity-100" : "opacity-0")} />
                            <div className="flex-1">
                              <span className="font-medium">{a.nome}</span>
                              <span className="text-xs text-slate-500 ml-2">({a.categoria} - Disp: {a.quantita_disponibile})</span>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* Volontario */}
            <div className="grid gap-2">
              <Label>Volontario *</Label>
              <Popover open={volSearchOpen} onOpenChange={setVolSearchOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" role="combobox" className="justify-between font-normal" data-testid="select-volontario">
                    {selectedVolontarioId
                      ? volontari.find(v => (v.volontario_id || v.persona_id) === selectedVolontarioId)?.nome_completo ||
                        `${volontari.find(v => (v.volontario_id || v.persona_id) === selectedVolontarioId)?.cognome || ''} ${volontari.find(v => (v.volontario_id || v.persona_id) === selectedVolontarioId)?.nome || ''}`.trim() || 'Selezionato'
                      : 'Cerca volontario...'}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0">
                  <Command>
                    <CommandInput placeholder="Cerca..." />
                    <CommandList>
                      <CommandEmpty>Nessun volontario trovato</CommandEmpty>
                      <CommandGroup>
                        {volontari.map(v => {
                          const vid = v.volontario_id || v.persona_id;
                          const vname = v.nome_completo || `${v.cognome || ''} ${v.nome || ''}`.trim();
                          return (
                            <CommandItem key={vid} onSelect={() => { setSelectedVolontarioId(vid); setVolSearchOpen(false); }}>
                              <Check className={cn("mr-2 h-4 w-4", selectedVolontarioId === vid ? "opacity-100" : "opacity-0")} />
                              {vname}
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Quantita</Label>
                <Input type="number" min="1" value={quantita} onChange={(e) => setQuantita(parseInt(e.target.value) || 1)} data-testid="input-quantita" />
              </div>
              <div className="grid gap-2">
                <Label>Taglia (opzionale)</Label>
                <Select value={taglia || 'nessuna'} onValueChange={(v) => setTaglia(v === 'nessuna' ? '' : v)}>
                  <SelectTrigger data-testid="select-taglia"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nessuna">Nessuna</SelectItem>
                    {TAGLIE.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid gap-2">
              <Label>Note</Label>
              <Input value={noteAssegnazione} onChange={(e) => setNoteAssegnazione(e.target.value)}
                placeholder="Note opzionali sulla consegna" data-testid="input-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewDialog(false)}>Annulla</Button>
            <Button onClick={handleAssegna} disabled={submitting} data-testid="conferma-assegnazione">
              {submitting ? 'Assegnazione...' : 'Conferma Assegnazione'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Restituzione */}
      <Dialog open={showResoDialog} onOpenChange={setShowResoDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Registra Restituzione</DialogTitle>
          </DialogHeader>
          {selectedAssegnazione && (
            <div className="grid gap-4 py-4">
              <div className="bg-slate-50 p-3 rounded-lg space-y-1">
                <p className="text-sm"><strong>Articolo:</strong> {selectedAssegnazione.articolo_nome}</p>
                <p className="text-sm"><strong>Volontario:</strong> {selectedAssegnazione.volontario_nome}</p>
                <p className="text-sm"><strong>Consegnato il:</strong> {formatDate(selectedAssegnazione.data_consegna)}</p>
              </div>
              <div className="grid gap-2">
                <Label>Condizione al reso</Label>
                <Select value={condizioneReso} onValueChange={setCondizioneReso}>
                  <SelectTrigger data-testid="select-condizione"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CONDIZIONI.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Note restituzione</Label>
                <Input value={noteReso} onChange={(e) => setNoteReso(e.target.value)}
                  placeholder="Note opzionali sulla restituzione" data-testid="input-note-reso" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResoDialog(false)}>Annulla</Button>
            <Button onClick={handleRestituisci} disabled={submitting} data-testid="conferma-reso">
              {submitting ? 'Registrazione...' : 'Registra Restituzione'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
